import React from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import FONT_FAMILIES from '../constants/fonts';

// Section Header Editor Component
const SectionHeaderEditor = ({ content, onChange }) => {
  const hasHeader = content.sectionHeader?.enabled;
  
  return (
    <div className="mb-3 pb-3 border-b border-slate-200">
      <label className="flex items-center gap-2 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={hasHeader || false}
          onChange={(e) => onChange({ 
            ...content, 
            sectionHeader: { 
              ...content.sectionHeader, 
              enabled: e.target.checked,
              text: content.sectionHeader?.text || '',
              headingTag: content.sectionHeader?.headingTag || 'h2',
              fontSize: content.sectionHeader?.fontSize || '28',
              fontFamily: content.sectionHeader?.fontFamily || 'inherit',
              color: content.sectionHeader?.color || '#1e293b',
              align: content.sectionHeader?.align || 'left',
              fontWeight: content.sectionHeader?.fontWeight || 'bold',
              marginTop: content.sectionHeader?.marginTop || '0',
              marginBottom: content.sectionHeader?.marginBottom || '16',
            }
          })}
          className="w-4 h-4 rounded border-slate-300"
        />
        <span className="text-xs font-medium text-slate-600">Add Section Header</span>
      </label>
      
      {hasHeader && (
        <div className="space-y-2 p-2 bg-slate-50 rounded-lg" onClick={(e) => e.stopPropagation()}>
          {/* Header Text */}
          <Input
            value={content.sectionHeader?.text || ''}
            onChange={(e) => onChange({ 
              ...content, 
              sectionHeader: { ...content.sectionHeader, text: e.target.value }
            })}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            placeholder="Section title (leave empty for spacer)"
            className="h-9 text-sm font-medium"
          />
          
          {/* Style Controls Row 1 */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Heading Tag (SEO) */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Tag:</label>
              <Select
                value={content.sectionHeader?.headingTag || 'h2'}
                onValueChange={(value) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, headingTag: value }
                })}
              >
                <SelectTrigger className="w-16 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="h1">H1</SelectItem>
                  <SelectItem value="h2">H2</SelectItem>
                  <SelectItem value="h3">H3</SelectItem>
                  <SelectItem value="h4">H4</SelectItem>
                  <SelectItem value="h5">H5</SelectItem>
                  <SelectItem value="h6">H6</SelectItem>
                  <SelectItem value="p">P</SelectItem>
                  <SelectItem value="div">DIV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Font Size */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Size:</label>
              <Select
                value={content.sectionHeader?.fontSize || '28'}
                onValueChange={(value) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, fontSize: value }
                })}
              >
                <SelectTrigger className="w-16 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">14</SelectItem>
                  <SelectItem value="16">16</SelectItem>
                  <SelectItem value="18">18</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="28">28</SelectItem>
                  <SelectItem value="32">32</SelectItem>
                  <SelectItem value="36">36</SelectItem>
                  <SelectItem value="42">42</SelectItem>
                  <SelectItem value="48">48</SelectItem>
                  <SelectItem value="56">56</SelectItem>
                  <SelectItem value="64">64</SelectItem>
                  <SelectItem value="72">72</SelectItem>
                  <SelectItem value="76">76</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Font Family */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Font:</label>
              <Select
                value={content.sectionHeader?.fontFamily || 'inherit'}
                onValueChange={(value) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, fontFamily: value }
                })}
              >
                <SelectTrigger className="w-28 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONT_FAMILIES.map((font) => (
                    <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                      {font.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Font Weight */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Weight:</label>
              <Select
                value={content.sectionHeader?.fontWeight || 'bold'}
                onValueChange={(value) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, fontWeight: value }
                })}
              >
                <SelectTrigger className="w-20 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="500">Medium</SelectItem>
                  <SelectItem value="600">Semi</SelectItem>
                  <SelectItem value="bold">Bold</SelectItem>
                  <SelectItem value="800">Extra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Text Color */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Color:</label>
              <input
                type="color"
                value={content.sectionHeader?.color || '#1e293b'}
                onChange={(e) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, color: e.target.value }
                })}
                className="w-7 h-7 rounded border cursor-pointer"
              />
            </div>
          </div>
          
          {/* Style Controls Row 2 */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Alignment */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Align:</label>
              <Select
                value={content.sectionHeader?.align || 'left'}
                onValueChange={(value) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, align: value }
                })}
              >
                <SelectTrigger className="w-20 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Top Margin (Space Above) */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Space above:</label>
              <Input
                type="number"
                value={content.sectionHeader?.marginTop || '0'}
                onChange={(e) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, marginTop: e.target.value }
                })}
                className="w-14 h-7 text-xs"
                min="0"
                max="100"
              />
              <span className="text-xs text-slate-400">px</span>
            </div>
            
            {/* Bottom Margin (Space Below) */}
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Space below:</label>
              <Input
                type="number"
                value={content.sectionHeader?.marginBottom || '16'}
                onChange={(e) => onChange({ 
                  ...content, 
                  sectionHeader: { ...content.sectionHeader, marginBottom: e.target.value }
                })}
                className="w-14 h-7 text-xs"
                min="0"
                max="100"
              />
              <span className="text-xs text-slate-400">px</span>
            </div>
          </div>
          
          {/* Preview */}
          <div className="mt-2 p-2 bg-white rounded border border-slate-200">
            <p className="text-[10px] text-slate-400 mb-1">Preview: &lt;{content.sectionHeader?.headingTag || 'h2'}&gt;</p>
            {React.createElement(
              content.sectionHeader?.headingTag || 'h2',
              {
                style: {
                  fontSize: `${content.sectionHeader?.fontSize || 28}px`,
                  fontFamily: content.sectionHeader?.fontFamily || 'inherit',
                  fontWeight: content.sectionHeader?.fontWeight || 'bold',
                  color: content.sectionHeader?.color || '#1e293b',
                  textAlign: content.sectionHeader?.align || 'left',
                  minHeight: content.sectionHeader?.text ? 'auto' : '20px',
                  margin: 0,
                }
              },
              content.sectionHeader?.text || <span className="text-slate-300 italic text-sm">(spacer)</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionHeaderEditor;
